import { useEffect, useState } from 'react';

/**
 * Ownership of ⌘K / Ctrl+K.
 *
 * Decision 2026-09-04: the shortcut opens the COMMAND PALETTE. It used to
 * open the AI chat, which is the rarer, more deliberate act — the chat moves
 * to ⌘J. Both sources consulted put navigation and commands on ⌘K, and it is
 * the key a user tries first.
 */
export function useCommandPaletteShortcut(onOpen: () => void, onOpenChat?: () => void) {
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const meta = event.metaKey || event.ctrlKey;
      if (!meta) return;
      // `event.key` is lowercase for an unmodified letter, but Shift or a
      // non-QWERTY layout can change it; comparing case-insensitively keeps
      // the shortcut working on an AZERTY keyboard.
      const key = event.key.toLowerCase();
      if (key === 'k') {
        event.preventDefault();
        onOpen();
      } else if (key === 'j' && onOpenChat) {
        event.preventDefault();
        onOpenChat();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onOpen, onOpenChat]);
}

/**
 * The commands the user reached most recently, newest first.
 *
 * « Display recent items before the user types anything » — an empty palette
 * that shows nothing teaches nothing and makes the second visit no faster
 * than the first. Stored per browser, never sent anywhere: it is a
 * convenience, and it would otherwise leak one user's navigation to another
 * on a shared device.
 */
const RECENTS_KEY = 'efacture.palette.recents';
const RECENTS_MAX = 5;

export function readRecents(): string[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    // Private window, cleared storage, quota — the palette works without it.
    return [];
  }
}

export function pushRecent(key: string): string[] {
  const next = [key, ...readRecents().filter((k) => k !== key)].slice(0, RECENTS_MAX);
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    // Ignored on purpose: failing to remember must never fail the navigation.
  }
  return next;
}

/** Debounced value — the palette must not query on every keystroke. */
export function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}
